/**
 * @fileoverview What to plant's one search (E19, design.md 2 row 6: one search, one results drawing, both pages): the
 * box, with its clear ✕, and the results that replace what is under it while something is typed.
 *
 * The results are the server's (E24; review of 29 Oct: the app decides nothing): what she typed goes to the server a
 * moment after she stops typing (useWhatToPlantSearch), and the answer is drawn as sent: its groups in order (hers
 * first with no heading, each row with its mark, "In your garden" or "From your to-do list"; then "Can go in now", "Not
 * now where you live", "No planting dates yet"), their rows (crops, types by name such as "Romaine Lettuce", her
 * plants with "Open it"), and the closing line with the count and what she typed; with nothing, the server's "Grow
 * doesn't know a plant called…" and its link to the Add a plant form. While the first answer is on its way, a calm line
 * (whatToPlant.catalogueLoading); a failure, the standard note.
 *
 * Where: WhatToPlantPage and WhatToPlantBrowsePage.
 * Words (en/es): whatToPlant.search.label, .placeholder, .clear; whatToPlant.catalogueLoading.
 *
 * @module CropSearch
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { AddCropState } from './useAddCrop';
import { CropRows } from './CropRows';
import { useWhatToPlantSearch } from './useWhatToPlantSearch';

/** What the box is handed. */
interface CropSearchBoxProps {
  /** What is typed. */
  query: string;
  /** Told what is typed. */
  onChange: (query: string) => void;
  /** The label above the box (What to plant's), or none (the See them page's box stands under its promise). */
  label?: string;
  /** The box itself, for "Search ›" to put the cursor in it. */
  inputRef?: React.Ref<HTMLInputElement>;
}

/**
 * The search box: a search icon, the box, and a clear ✕ while something is typed.
 *
 * @returns The box.
 */
export const CropSearchBox: React.FC<CropSearchBoxProps> = ({ query, onChange, label, inputRef }) => {
  const { reactTranslate: t } = useTranslation();
  const id = React.useId();
  return (
    <div className="mt-4">
      {label && <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-foreground">{label}</label>}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          id={id}
          ref={inputRef}
          type="search"
          value={query}
          onChange={(event) => onChange(event.target.value)}
          placeholder={t('whatToPlant.search.placeholder')}
          aria-label={label ? undefined : t('whatToPlant.search.placeholder')}
          className="h-10 bg-card pl-9 pr-10 text-base [&::-webkit-search-cancel-button]:hidden"
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label={t('whatToPlant.search.clear')}
            className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

/** What the results are handed. */
interface CropSearchResultsProps {
  /** What is typed (not empty). */
  query: string;
  /** The add's state. */
  adder: AddCropState;
  /** The server's "Added" and "Open it". */
  addedWords: { word: string; open: string };
}

/**
 * The results: the server's answer for what she typed, drawn as sent.
 *
 * @returns The results.
 */
export const CropSearchResults: React.FC<CropSearchResultsProps> = ({ query, adder, addedWords }) => {
  const { reactTranslate: t } = useTranslation();
  const { data, isLoading, error } = useWhatToPlantSearch(query);
  if (error) return <ServerFailureNote error={error} where="whatToPlantSearch" className="mt-5" />;
  if (isLoading || !data) return <p className="mt-5 text-sm text-muted-foreground" aria-busy="true">{t('whatToPlant.catalogueLoading')}</p>;
  if (data.nothing) {
    return (
      <p className="mt-5 text-sm text-muted-foreground">
        {data.nothing.text}
        {' '}
        {data.nothing.addYourself.href && (
          <Link to={data.nothing.addYourself.href} className="font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {data.nothing.addYourself.text}
            {' ›'}
          </Link>
        )}
      </p>
    );
  }
  return (
    <div aria-live="polite">
      {data.groups.map((group) => (
        <section key={group.key} className="mt-5" aria-label={group.heading ?? undefined}>
          {group.heading && <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.heading}</h2>}
          {group.sub && <p className="mt-1 text-sm text-muted-foreground">{group.sub}</p>}
          <div className="mt-2">
            <CropRows rows={group.rows} adder={adder} addedWords={addedWords} />
          </div>
        </section>
      ))}
      {data.closing && <p className="mt-4 text-sm text-muted-foreground">{data.closing}</p>}
    </div>
  );
};
